import { FC, ReactNode } from 'react'
import classNames from 'classnames'
import Tooltip from 'components/base/Tooltip'
import Input, { InputType } from './Input'
import Icon from 'project/Icon'
import { GUID } from 'common/utils/string/GUID'

export interface InputGroupProps extends Omit<InputType, 'title'> {
  action?: ReactNode
  component?: ReactNode
  error?: string
  errorMessage?: string
  inputProps?: InputType
  required?: boolean
  title?: ReactNode
  tooltip?: string
}

const InputGroup: FC<InputGroupProps> = ({
  action,
  className,
  component,
  defaultValue,
  disabled,
  error,
  errorMessage,
  iconLeft,
  id: providedId,
  inputProps = {},
  inputRef,
  isValid,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  textarea,
  title,
  tooltip,
  touched,
  type = 'text',
  value,
}) => {
  const id = providedId || name || inputProps.id || inputProps.name || GUID()

  const computedIsValid =
    isValid !== undefined ? isValid : touched && error ? false : undefined

  const computedErrorMessage =
    errorMessage !== undefined
      ? errorMessage
      : touched && error
      ? error
      : undefined

  const errorId = inputProps.name ? `${inputProps.name}-error` : undefined

  return (
    <div className={classNames('form-group', className)}>
      {(title || action) && (
        <div className='flex-row'>
          <Tooltip place='top' content={tooltip}>
            <label
              className='form-label d-flex align-items-center fw-semibold flex-fill'
              htmlFor={id}
            >
              {title}
              {tooltip && (
                <Icon
                  width={18}
                  fill='dark300'
                  name='info'
                  className='mx-2 text-dark300'
                />
              )}
              {required && <span className='text-danger'>*</span>}
            </label>
            {action}
          </Tooltip>
        </div>
      )}

      {component ?? (
        <Input
          {...inputProps}
          data-test={(inputProps as Record<string, unknown>)['data-test']}
          defaultValue={defaultValue}
          disabled={disabled}
          iconLeft={iconLeft}
          id={id}
          inputRef={inputRef}
          isValid={computedIsValid}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          placeholder={placeholder}
          textarea={textarea}
          touched={touched}
          type={type}
          value={value}
        />
      )}

      {computedErrorMessage && (
        <span id={errorId} className='text-danger'>
          {computedErrorMessage}
        </span>
      )}
    </div>
  )
}

InputGroup.displayName = 'InputGroup'
export default InputGroup
